import { useAppState } from "../../state/App";
import React, { useRef, useState, useEffect } from "react";
import ChatHistoryList from "./ChatHistoryList";
import ChatHistorySearchResults, {
  SEARCH_SNIPPET_MAX_LENGTH,
} from "./ChatHistorySearchResults";
import {
  Typography,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  InputBase,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useConversationHistory } from "../../hooks/useConversationHistory";
import { useConversationSearch } from "../../hooks/useConversationSearch";
import { commonMessages } from "../../commonMessages";
import { defineMessages, useIntl } from "react-intl";
import { useConversationsDelete } from "../../hooks/useConversationsDelete";
import { useSnackbar } from "@myplant-io/snackbar";

const messages = defineMessages({
  clearAllConfirmMessage: {
    id: "chatHistory.clearAllConfirm.message",
    defaultMessage: "All chat history will be permanently removed.",
  },
  clearAllAction: {
    id: "chatHistory.clearAllConfirm.action",
    defaultMessage: "Clear All",
  },
  clearAllError: {
    id: "chatHistory.clearAllError",
    defaultMessage:
      "Error deleting all chat history. Please try again or contact the site administrator.",
  },
});

export enum ChatHistoryPanelTabs {
  History = "History",
}

export function ChatHistoryPanel() {
  const { formatMessage: t } = useIntl();
  const { showErrorMessage, showInfoMessage } = useSnackbar();
  const conversationHistory = useConversationHistory();
  const {
    actions: { setCurrentConversationId, setMessages },
  } = useAppState();
  const { mutateAsync: deleteConversations } = useConversationsDelete();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const searchResults = useConversationSearch(
    debouncedQuery,
    SEARCH_SNIPPET_MAX_LENGTH
  );
  const isSearchActive = debouncedQuery.length > 0;
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openSearch = () => {
    setIsSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchInput("");
    setDebouncedQuery("");
  };

  const [ctxMenuAnchorEl, setCtxMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const ctxMenuOpen = Boolean(ctxMenuAnchorEl);
  const onShowContextualMenu = (event: React.MouseEvent<HTMLElement>) => {
    setCtxMenuAnchorEl(event.currentTarget);
  };
  const onHideContextualMenu = () => {
    setCtxMenuAnchorEl(null);
  };

  const onClearAllChatHistory = async () => {
    try {
      await deleteConversations();
      setCurrentConversationId(undefined);
      setMessages([]);
    } catch (error) {
      showErrorMessage({ message: t(messages.clearAllError) });
    }
  };

  const onConfirmClearAll = () => {
    showInfoMessage({
      message: t(messages.clearAllConfirmMessage),
      action: t(messages.clearAllAction),
      handleAction: onClearAllChatHistory,
    });
  };

  return (
    <section
      className="mt-4 mx-2 overflow-auto"
      aria-label={"chat history panel"}
    >
      <div
        aria-label="chat history header"
        className="w-full mb-2 flex justify-between items-center"
        style={{ minHeight: 40 }}
      >
        {isSearchOpen ? (
          <>
            <InputBase
              inputRef={searchInputRef}
              fullWidth
              placeholder={t(commonMessages.search)}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") closeSearch();
              }}
              onBlur={() => {
                if (!searchInput.trim()) closeSearch();
              }}
              aria-label="search chat history"
              startAdornment={
                <SearchIcon fontSize="small" color="action" className="mr-1" />
              }
              style={{ fontSize: "0.95rem" }}
            />
            <IconButton
              size="small"
              onClick={closeSearch}
              aria-label="close search"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Typography variant="h6" role="heading" aria-level={2}>
              Chat history
            </Typography>
            <div>
              <IconButton
                size="small"
                title={t(commonMessages.search)}
                onClick={openSearch}
                aria-label="search chat history"
              >
                <SearchIcon fontSize="small" />
              </IconButton>
              <IconButton
                title={"Clear all chat history"}
                onClick={onShowContextualMenu}
                aria-label={"clear all chat history"}
                role="button"
                id="moreButton"
                size="small"
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </div>
          </>
        )}
      </div>
      {conversationHistory.error && !isSearchActive ? (
        <div className="mt-2 flex flex-col items-center">
          <div>
            <Typography variant="caption">
              Error loading chat history
            </Typography>
          </div>
          <div>
            <Typography variant="caption">
              Chat history can&apos;t be saved at this time
            </Typography>
          </div>
        </div>
      ) : isSearchActive ? (
        <ChatHistorySearchResults
          results={searchResults.data ?? []}
          isLoading={searchResults.isLoading}
          query={debouncedQuery}
        />
      ) : (
        <ChatHistoryList />
      )}
      {conversationHistory.isLoading && !isSearchActive && (
        <>
          <div className="mt-4 flex justify-center">
            <div className="mr-2 flex items-center">
              <CircularProgress size={16} />
            </div>
            <Typography variant="caption">Loading chat history</Typography>
          </div>
        </>
      )}
      <Menu
        id="context-menu"
        anchorEl={ctxMenuAnchorEl}
        open={ctxMenuOpen}
        onClose={onHideContextualMenu}
        slotProps={{
          list: {
            "aria-labelledby": "context-menu",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setCtxMenuAnchorEl(null);
            onConfirmClearAll();
          }}
        >
          <DeleteIcon className="mr-2" />
          Clear all chat history
        </MenuItem>
      </Menu>
    </section>
  );
}
